import { Toggle } from "@base-ui/react/toggle";
import { ToggleGroup as Base } from "@base-ui/react/toggle-group";
import type { ReactNode } from "react";
import { Tooltip } from "./feedback";

export interface ToggleItem<V extends string> {
  value: V;
  label: ReactNode;
  /** A tooltip (on hover and keyboard focus). */
  title?: string;
  disabled?: boolean;
}

interface Common<V extends string> {
  items: readonly ToggleItem<V>[];
  /** What the buttons choose, for screen readers ("Show services"). */
  label: string;
  /** The row's class, for the look: `chips` by default; apps keep their own (booking-desk's `seg` segmented control). */
  className?: string;
  /** Each button's class (`chip` by default), or a function of its value; the pressed ones also get `on`. */
  itemClassName?: string | ((value: V) => string);
  disabled?: boolean;
}

/**
 * A row of toggle buttons on Base UI's ToggleGroup: one Tab stop for the row, arrow keys between the
 * buttons, aria-pressed on each. Single choice (a segmented control, a filter: one value, never none) or,
 * with `multiple`, any number (weekdays, times).
 */
export type ToggleGroupProps<V extends string> = Common<V> & (
  | { multiple?: false; value: V; onValueChange: (value: V) => void }
  | { multiple: true; value: readonly V[]; onValueChange: (value: V[]) => void }
);

export function ToggleGroup<V extends string>(props: ToggleGroupProps<V>) {
  const { items, label, className = "chips", itemClassName = className === "chips" ? "chip" : undefined, disabled } = props;
  const pressed: readonly V[] = props.multiple ? props.value : [props.value];
  const change = (next: string[]) => {
    if (props.multiple) props.onValueChange(next as V[]);
    // A single choice can't be emptied by pressing the current one again.
    else if (next[0] !== undefined && next[0] !== props.value) props.onValueChange(next[0] as V);
  };
  return (
    <Base value={[...pressed]} onValueChange={change} multiple={props.multiple ?? false} disabled={disabled} className={className} aria-label={label}>
      {items.map((t) => {
        const own = typeof itemClassName === "function" ? itemClassName(t.value) : itemClassName;
        const cls = [own, pressed.includes(t.value) ? "on" : ""].filter(Boolean).join(" ");
        const toggle = (
          <Toggle key={t.value} value={t.value} disabled={t.disabled} className={cls || undefined}>
            {t.label}
          </Toggle>
        );
        return t.title ? <Tooltip key={t.value} content={t.title}>{toggle}</Tooltip> : toggle;
      })}
    </Base>
  );
}
